import React, { useState, useEffect, useMemo, useRef, useCallback, createRef } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { supabase } from '../../lib/supabase';
import {
  updateTranslationKey,
  upsertTranslationString,
  createTranslationKey,
  deleteTranslationKey,
  checkKeyExists,
  TranslationKey,
  fetchAllTranslationData,
  TranslationData
} from '../../services/translationService';
import { aiTranslationService } from '../../services/translationAI';
import EmptyStateEnhanced from '../../components/EmptyStateEnhanced';
import Button from '../../components/Button/Button';
import TranslationTableSection from '../../components/EditableTable/TranslationTableSection';
import TranslationKeyDialog from '../../components/TranslationKeyDialog/TranslationKeyDialog';
import EditKeyDialog from '../../components/EditKeyDialog/EditKeyDialog';
import SelectionActionBar from '../../components/SelectionActionBar';
import ReviewDialog from '../../components/ReviewDialog/ReviewDialog';
import SelectReviewDialog from '../../components/SelectReviewDialog/SelectReviewDialog';
import ImportTranslationsDialog from '../../components/ImportTranslationsDialog/ImportTranslationsDialog';
import ExportTranslationsDialog from '../../components/ExportTranslationsDialog';
import ContentCard from '../../components/ContentCard/ContentCard';
import LanguageTabs from '../../components/LanguageTabs/LanguageTabs';
import '../SharedStyles.css';
import './TranslationsView.css';
import { useLanguages } from '../../context/LanguageContext';
import { useProjectPermissions } from '../../hooks/useProjectPermissions';
import { setContentWithHighlightedPlaceholders } from '../../utils/placeholderUtils';
import { batchTranslationService, type BatchTranslationRequest } from '../../services/batchTranslationService';
import { translationGrouper } from '../../utils/translationGrouping';
import { useSubscription, FREE_TIER_KEY_LIMIT } from '../../hooks/useSubscription';
import SubscriptionDialog from '../../components/SubscriptionDialog/SubscriptionDialog';


const TranslationsView: React.FC = React.memo(() => {
  const { selectedProject, selectedTeam, projects } = useWorkspace();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [translationData, setTranslationData] = useState<TranslationData[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isKeyDialogOpen, setIsKeyDialogOpen] = useState(false);
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [keyToEdit, setKeyToEdit] = useState<TranslationKey | null>(null);
  const [isEditingKey, setIsEditingKey] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [isCreatingReview, setIsCreatingReview] = useState(false);
  const [isSelectReviewDialogOpen, setIsSelectReviewDialogOpen] = useState(false);
  const [isAddingToReview, setIsAddingToReview] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isImporting] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isExporting] = useState(false);
  const [activeLanguageJobs, setActiveLanguageJobs] = useState<Map<string, { jobId: string; progress: any }>>(new Map());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setter used for completion tracking
  const [completedTranslations, setCompletedTranslations] = useState<Set<string>>(new Set());
  const [completionStates, setCompletionStates] = useState<Map<string, any>>(new Map());
  const [verificationCleanups, setVerificationCleanups] = useState<Map<string, () => void>>(new Map());
  const [preparingLanguages, setPreparingLanguages] = useState<Set<string>>(new Set());
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscription();
  const projectKeyCount = translationData.length;
  const showSubscriptionBlocker = !isSubscriptionLoading && !hasActiveSubscription && projectKeyCount >= FREE_TIER_KEY_LIMIT;
  const { 
    selectedLanguage,
    firstLanguage,
    getLanguageName,
    setSelectedLanguage,
    availableLanguages,
    addLanguage
  } = useLanguages();

  // Create stable callback for language selection
  const handleLanguageSelect = useCallback((language: string) => {
    setSelectedLanguage(language);
  }, [setSelectedLanguage]);

  // Wrapper for addLanguage with subscription check
  const handleAddLanguage = useCallback(async (language: string) => {
    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return;
    }
    await addLanguage(language);
  }, [showSubscriptionBlocker, addLanguage]);

  const { isAdmin, canEditLanguages } = useProjectPermissions();

  // Cache for language stats to improve performance
  const languageStatsCache = useRef<{
    key: string;
    stats: any[];
  } | null>(null);

  // State for action bar
  const [, setIsInputFocused] = useState(false);
  const [, setFocusedCellInfo] = useState<{
    rowKey: string;
    columnKey: string;
    content: string;
    sourceLanguage: string | null;
    sourceText: string;
  } | null>(null);

  // State for cross-section tab navigation and infinite scrolling
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [renderedSections, setRenderedSections] = useState<Set<number>>(new Set([0, 1, 2, 3, 4]));
  const sectionRefs = useRef<{ [prefix: string]: React.RefObject<HTMLDivElement> }>({});
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const isLoadingMore = useRef(false);
  const [isLoadingMoreSections, setIsLoadingMoreSections] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreSectionsRef = useRef<() => void>(() => {});
  
  // Function to find which section contains the currently focused element
  const findCurrentSectionIndex = () => {
    const activeElement = document.activeElement;
    if (!activeElement) return 0;
    
    // Find which section contains the active element
    for (let i = 0; i < sortedPrefixes.length; i++) {
      const prefix = sortedPrefixes[i];
      const sectionRef = sectionRefs.current[prefix];
      if (sectionRef?.current && sectionRef.current.contains(activeElement)) {
        return i;
      }
    }
    return 0;
  };





  // Function to refresh only the table data without affecting UI state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for refresh
  const refreshTableData = async () => {
    if (!selectedProject) {
      return;
    }
    
    try {
      // Clear the translation grouper cache to ensure fresh data
      translationGrouper.clearCache();
      
      // Clear the data cache to force fresh fetch
      translationDataCache.current.delete(selectedProject.id);
      
      // Fetch fresh translation data
      const allTranslations = await fetchAllTranslationData(selectedProject.id);
      
      // Update cache with fresh data
      translationDataCache.current.set(selectedProject.id, {
        data: allTranslations,
        timestamp: Date.now()
      });
      
      setTranslationData(allTranslations);
    } catch (err) {
      // Failed to refresh table data
    }
  };

  // Ensure a language is selected on load
  useEffect(() => {
    // If no language is selected, select the first available language (source)
    if (!selectedLanguage && availableLanguages.length > 0) {
      const firstLang = availableLanguages[0];
      handleLanguageSelect(firstLang);
    }
  }, [selectedLanguage, availableLanguages, handleLanguageSelect]);

  // Simple in-memory cache for translation data
  const translationDataCache = useRef<Map<string, { data: TranslationData[]; timestamp: number }>>(new Map());
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const loadTranslations = async () => {
    if (!selectedProject) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Check cache first
      const cacheKey = selectedProject.id;
      const cached = translationDataCache.current.get(cacheKey);
      const now = Date.now();
      
      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        // Use cached data for instant load
        setTranslationData(cached.data);
        setIsLoading(false);
        
        // Refresh completion states in background (non-blocking)
        refreshCompletionStates().catch(() => {
          // Silently handle errors in background refresh
        });
        
        // Fetch fresh data in background and update cache
        fetchAllTranslationData(selectedProject.id)
          .then((freshData) => {
            translationDataCache.current.set(cacheKey, {
              data: freshData,
              timestamp: Date.now()
            });
            setTranslationData(freshData);
            // Refresh completion states with fresh data
            refreshCompletionStates().catch(() => {});
          })
          .catch(() => {
            // Silently handle errors in background fetch
          });
        
        return;
      }
      
      // Fetch all keys and their strings in one optimized query
      const allTranslations = await fetchAllTranslationData(selectedProject.id);
      
      // Update cache
      translationDataCache.current.set(cacheKey, {
        data: allTranslations,
        timestamp: Date.now()
      });
      
      setTranslationData(allTranslations);
      
      // Refresh completion states in background (non-blocking) - don't await
      refreshCompletionStates().catch(() => {
        // Silently handle errors in background refresh
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Clear cache when project changes
    if (selectedProject) {
      translationDataCache.current.delete(selectedProject.id);
    }
    loadTranslations();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadTranslations stable, run on selectedProject only
  }, [selectedProject]);

  // Initialize completion states for all languages when project loads (non-blocking)
  useEffect(() => {
    const initializeCompletionStates = async () => {
      if (!selectedProject?.id || !firstLanguage || !availableLanguages) return;

      // Process languages in parallel instead of sequentially
      const languagePromises = availableLanguages
        .filter(language => language !== firstLanguage)
        .map(async (language) => {
          try {
            const completionState = await batchTranslationService.verifyTranslationCompletion(
              selectedProject.id,
              language,
              firstLanguage
            );
            
            return {
              language,
              completionState,
              cleanup: batchTranslationService.setupCompletionVerification(
                selectedProject.id,
                language,
                firstLanguage,
                (newState) => {
                  setCompletionStates(prev => {
                    const newMap = new Map(prev);
                    newMap.set(language, newState);
                    return newMap;
                  });
                }
              )
            };
          } catch (error) {
            return null;
          }
        });

      // Wait for all languages to complete, but don't block UI
      // Use React.startTransition to make state updates non-blocking
      Promise.all(languagePromises).then((results) => {
        const newCompletionStates = new Map();
        const newVerificationCleanups = new Map();
        
        results.forEach((result) => {
          if (result) {
            newCompletionStates.set(result.language, result.completionState);
            newVerificationCleanups.set(result.language, result.cleanup);
          }
        });

        // Use startTransition to make state updates non-blocking
        React.startTransition(() => {
          setCompletionStates(newCompletionStates);
          setVerificationCleanups(newVerificationCleanups);
        });
      });
    };

    // Small delay to let initial render complete first
    const timeoutId = setTimeout(() => {
      initializeCompletionStates();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedProject?.id, firstLanguage, availableLanguages]);

  // Cleanup cache on unmount
  useEffect(() => {
    return () => {
      translationGrouper.clearCache();
      // Clean up verification intervals
      verificationCleanups.forEach(cleanup => cleanup());
      batchTranslationService.clearCompletionCache(selectedProject?.id || '');
      // Clean up debounced refresh timeout
      if (debouncedRefreshCompletionStates.current) {
        clearTimeout(debouncedRefreshCompletionStates.current);
      }
    };
  }, [verificationCleanups, selectedProject?.id]);

  // Subscribe to real-time translation updates for immediate UI feedback
  useEffect(() => {
    if (!selectedProject?.id) return;

    // Throttle rapid updates to prevent browser resource exhaustion
    let updateQueue: any[] = [];
    let updateTimeout: NodeJS.Timeout | null = null;
    let unsubscribe: (() => void) | null = null;
    let isManualEditActive = false;

    const processUpdates = () => {
      if (updateQueue.length === 0) return;


      
      // Update all queued translations in one state update
      setTranslationData(prevData => {
        let newData = [...prevData];
        
        updateQueue.forEach(newTranslation => {
          newData = newData.map(item => {
            if (item.key?.id === newTranslation.key_id) {
              // Update or add the translation string
              const updatedStrings = item.strings.filter(
                s => s.language_code !== newTranslation.language_code
              );
              updatedStrings.push(newTranslation);
              
              return {
                ...item,
                strings: updatedStrings
              };
            }
            return item;
          });
        });
        
        return newData;
      });

      // Show success indicators for all updated translations
      updateQueue.forEach(newTranslation => {
        const successClass = 'translation-success';
        const cellElement = document.querySelector(
          `.editable-table-cell[data-row="${newTranslation.key_id}"][data-col="${newTranslation.language_code}"]`
        );
        if (cellElement) {
          cellElement.classList.add(successClass);
          setTimeout(() => {
            cellElement.classList.remove(successClass);
          }, 1000);
        }
      });

      // Clear the queue
      updateQueue = [];
      updateTimeout = null;
    };

    const setupSubscription = () => {
      if (isManualEditActive) return; // Don't setup if manual edit is active
      
      unsubscribe = batchTranslationService.subscribeToTranslationUpdates(
        selectedProject.id,
        (newTranslation) => {
          // Skip real-time updates if we're in the middle of a manual edit
          if (isManualEditActive) {
    
            return;
          }
          
          // Add to queue and throttle updates
          updateQueue.push(newTranslation);
          
          if (updateTimeout) {
            clearTimeout(updateTimeout);
          }
          
          // Process updates after a delay to batch them - increased delay
          updateTimeout = setTimeout(processUpdates, 2000);
        }
      );
    };

    const disableSubscription = () => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      isManualEditActive = true;
    };

    const enableSubscription = () => {
      isManualEditActive = false;
      setTimeout(() => {
        if (!isManualEditActive) {
          setupSubscription();
        }
      }, 2000); // Wait 2 seconds before re-enabling
    };

    // Initial setup
    setupSubscription();

    // Listen for manual edit events
    const checkManualEdit = () => {
      const manualEditId = window.sessionStorage.getItem('texta_manual_edit');
      if (manualEditId && !isManualEditActive) {

        disableSubscription();
      } else if (!manualEditId && isManualEditActive) {
        
        enableSubscription();
      }
    };

    // Check periodically for manual edit state changes - reduced frequency
    const intervalId = setInterval(checkManualEdit, 500);

    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      if (unsubscribe) {
        unsubscribe();
      }
      clearInterval(intervalId);
    };
  }, [selectedProject?.id]);

  // Comprehensive active job monitoring with real-time updates and periodic polling
  useEffect(() => {
    let unsubscribeFunctions: (() => void)[] = [];
    let pollingInterval: NodeJS.Timeout | null = null;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- guard for cleanup
    let isSubscribed = false;

    const setupJobMonitoring = async () => {
      if (!selectedProject?.id) return;

      try {
        // Get all active jobs for all languages
        const allActiveJobs = await batchTranslationService.getAllActiveJobs(selectedProject.id);
        
        if (allActiveJobs.size > 0) {
          // Set the active language jobs state with correct structure
          const formattedActiveJobs = new Map<string, { jobId: string; progress: any }>();
          allActiveJobs.forEach((job, targetLanguage) => {
            formattedActiveJobs.set(targetLanguage, {
              jobId: job.id,
              progress: job
            });
          });
          setActiveLanguageJobs(formattedActiveJobs);
          
          // Subscribe to real-time progress updates for all active jobs
          allActiveJobs.forEach((job, targetLanguage) => {
            const unsubscribe = batchTranslationService.subscribeToProgress(job.id, {
              onProgress: (progress) => {
                setActiveLanguageJobs(prev => {
                  const newMap = new Map(prev);
                  newMap.set(targetLanguage, {
                    jobId: progress.id,
                    progress: progress
                  });
                  return newMap;
                });
                
                const progressText = `Translating ${targetLanguage}... ${progress.progress_percentage}% (${progress.completed_items}/${progress.total_items})`;
                showNotification(progressText);
              },
              onComplete: async (progress) => {
                // Remove from active jobs
                setActiveLanguageJobs(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(targetLanguage);
                  return newMap;
                });
                
                // Verify actual completion state from backend
                if (selectedProject?.id && firstLanguage) {
                  try {
                    const completionState = await batchTranslationService.verifyTranslationCompletion(
                      selectedProject.id,
                      targetLanguage,
                      firstLanguage
                    );
                    
                    // Update completion states
                    setCompletionStates(prev => {
                      const newMap = new Map(prev);
                      newMap.set(targetLanguage, completionState);
                      return newMap;
                    });
                    
                    // Set up periodic verification for this language
                    const cleanup = batchTranslationService.setupCompletionVerification(
                      selectedProject.id,
                      targetLanguage,
                      firstLanguage,
                      (newState) => {
                        setCompletionStates(prev => {
                          const newMap = new Map(prev);
                          newMap.set(targetLanguage, newState);
                          return newMap;
                        });
                      }
                    );
                    
                    // Store cleanup function
                    setVerificationCleanups(prev => {
                      const newMap = new Map(prev);
                      newMap.set(targetLanguage, cleanup);
                      return newMap;
                    });
                    
                    // Mark as completed only if actually complete
                    if (completionState.isComplete) {
                      setCompletedTranslations(prev => new Set(Array.from(prev).concat(targetLanguage)));
                    }
                    
                    // Clear the language stats cache to force recalculation
                    languageStatsCache.current = null;
                    
                  } catch (error) {
                    // Error verifying completion state
                  }
                }
                
                // Reload translations to show updated data
                await loadTranslations();
                
                if (progress.failed_items > 0) {
                  showNotification(`✅ ${targetLanguage} translation complete! ${progress.completed_items} successful, ${progress.failed_items} failed`);
                } else {
                  showNotification(`✅ ${targetLanguage} translation complete! ${progress.completed_items} keys successfully translated`);
                }
              },
              onError: (error) => {
                // Remove from active jobs
                setActiveLanguageJobs(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(targetLanguage);
                  return newMap;
                });
                
                showNotification(`❌ ${targetLanguage} translation failed: ${error}`);
              }
            });
            
            unsubscribeFunctions.push(unsubscribe);
          });
          
          isSubscribed = true;
        }
      } catch (error) {
        // Error setting up job monitoring
      }
    };

        const pollForUpdates = async () => {
      if (!selectedProject?.id) return;

      try {
        // Get current active jobs from state
        const currentJobs = Array.from(activeLanguageJobs.entries());
        
        if (currentJobs.length > 0) {
          let hasChanges = false;
          const updatedJobs = new Map<string, { jobId: string; progress: any }>();
          
          // Poll each active job individually for more efficient updates
          for (const [targetLanguage, currentJob] of currentJobs) {
            const latestProgress = await batchTranslationService.getJobProgress(currentJob.jobId);
            
            if (latestProgress) {
              
              // Check if job is still active
              if (['pending', 'processing'].includes(latestProgress.status)) {
                updatedJobs.set(targetLanguage, {
                  jobId: latestProgress.id,
                  progress: latestProgress
                });
                
                // Check if progress has changed
                if (currentJob.progress.progress_percentage !== latestProgress.progress_percentage) {
                  hasChanges = true;
                }
              } else {
                // Job completed or failed - remove from active jobs
                hasChanges = true;
                // Don't add to updatedJobs, so it gets removed
              }
            } else {
              // Job not found, might have been deleted
              hasChanges = true;
            }
          }
          
          if (hasChanges) {
            setActiveLanguageJobs(updatedJobs);
            
            // If any jobs were completed, update completion states for those languages
            if (updatedJobs.size < currentJobs.length) {
              const completedLanguages = currentJobs.filter(([lang, job]) => !updatedJobs.has(lang)).map(([lang]) => lang);
              
              // Update completion states for completed languages
              for (const language of completedLanguages) {
                if (selectedProject?.id && firstLanguage) {
                  batchTranslationService.verifyTranslationCompletion(
                    selectedProject.id,
                    language,
                    firstLanguage
                  ).then(completionState => {
                    setCompletionStates(prev => {
                      const newMap = new Map(prev);
                      newMap.set(language, completionState);
                      return newMap;
                    });
                  });
                }
              }
            }
          }
        } else {
          // No current jobs, check if new jobs were started
          const allActiveJobs = await batchTranslationService.getAllActiveJobs(selectedProject.id);
          if (allActiveJobs.size > 0) {
            const formattedActiveJobs = new Map<string, { jobId: string; progress: any }>();
            allActiveJobs.forEach((job, targetLanguage) => {
              formattedActiveJobs.set(targetLanguage, {
                jobId: job.id,
                progress: job
              });
            });
            
            setActiveLanguageJobs(formattedActiveJobs);
          }
        }
      } catch (error) {
        // Error polling for job updates
      }
    };

    // Initial setup
    setupJobMonitoring();

    // Set up periodic polling every 10 seconds as backup (increased from 5 seconds)
    pollingInterval = setInterval(pollForUpdates, 10000);
    
    // Check for completed jobs every 30 seconds (increased from 15 seconds)
    const completionCheckInterval = setInterval(async () => {
      if (selectedProject?.id && activeLanguageJobs.size > 0) {
        try {
          const allActiveJobs = await batchTranslationService.getAllActiveJobs(selectedProject.id);
          const currentJobLanguages = Array.from(activeLanguageJobs.keys());
          
          // Check if any of our tracked jobs are no longer active
          for (const language of currentJobLanguages) {
            if (!allActiveJobs.has(language)) {
              setActiveLanguageJobs(prev => {
                const newMap = new Map(prev);
                newMap.delete(language);
                return newMap;
              });
              
              // Update completion state for this language
              if (selectedProject?.id && firstLanguage) {
                batchTranslationService.verifyTranslationCompletion(
                  selectedProject.id,
                  language,
                  firstLanguage
                ).then(completionState => {
                  setCompletionStates(prev => {
                    const newMap = new Map(prev);
                    newMap.set(language, completionState);
                    return newMap;
                  });
                });
              }
            }
          }
        } catch (error) {
          // Error checking for completed jobs
        }
      }
    }, 30000);

    // Cleanup function
    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      if (completionCheckInterval) {
        clearInterval(completionCheckInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: setup job monitoring on project change only
  }, [selectedProject?.id]);

  // Listen for search query updates from the toolbar
  useEffect(() => {
    const handleSearchUpdate = (event: CustomEvent<{ searchQuery: string }>) => {
      setSearchQuery(event.detail.searchQuery);
    };

    window.addEventListener('update-search-query', handleSearchUpdate as EventListener);
    return () => {
      window.removeEventListener('update-search-query', handleSearchUpdate as EventListener);
    };
  }, []);

  // Listen for new key dialog open event from toolbar
  useEffect(() => {
    const onOpenKeyDialog = () => {
      setIsKeyDialogOpen(true);
    };

    window.addEventListener('open-key-dialog', onOpenKeyDialog);
    return () => {
      window.removeEventListener('open-key-dialog', onOpenKeyDialog);
    };
  }, []);

  // Listen for import dialog open event from toolbar
  useEffect(() => {
    const onOpenImportDialog = () => {
      setIsImportDialogOpen(true);
    };

    window.addEventListener('open-import-dialog', onOpenImportDialog);
    return () => {
      window.removeEventListener('open-import-dialog', onOpenImportDialog);
    };
  }, []);

  // Listen for export dialog open event from toolbar
  useEffect(() => {
    const onOpenExportDialog = () => {
      setIsExportDialogOpen(true);
    };

    window.addEventListener('open-export-dialog', onOpenExportDialog);
    return () => {
      window.removeEventListener('open-export-dialog', onOpenExportDialog);
    };
  }, []);

  // Use optimized grouping with memoization
  const groupedTranslations = useMemo(() => {
    return translationGrouper.groupTranslations(translationData, searchQuery);
  }, [translationData, searchQuery]);
  
  const sortedPrefixes = useMemo(() => {
    return translationGrouper.getSortedPrefixes(groupedTranslations);
  }, [groupedTranslations]);

  // Function to progressively render more sections
  const loadMoreSections = useCallback(() => {
    if (isLoadingMore.current) return;
    
    isLoadingMore.current = true;
    setIsLoadingMoreSections(true);
    
    setRenderedSections(prev => {
      const newSet = new Set(prev);
      const maxRendered = Math.max(...Array.from(prev));
      // Add next 5 sections
      for (let i = maxRendered + 1; i <= Math.min(maxRendered + 5, sortedPrefixes.length - 1); i++) {
        newSet.add(i);
      }
      return newSet;
    });
    
    // Reset loading state after a short delay to prevent rapid triggering
    setTimeout(() => {
      isLoadingMore.current = false;
      setIsLoadingMoreSections(false);
    }, 100);
  }, [sortedPrefixes.length]);

  // Keep ref updated with latest loadMoreSections function
  useEffect(() => {
    loadMoreSectionsRef.current = loadMoreSections;
  }, [loadMoreSections]);

  // Update current section index when focus changes
  useEffect(() => {
    const handleFocusChange = () => {
      const newSectionIndex = findCurrentSectionIndex();
      if (newSectionIndex !== currentSectionIndex) {
        setCurrentSectionIndex(newSectionIndex);
      }
    };

    // Listen for focus changes on editable cells
    document.addEventListener('focusin', handleFocusChange);

    return () => {
      document.removeEventListener('focusin', handleFocusChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- findCurrentSectionIndex reads DOM/refs
  }, [currentSectionIndex, sortedPrefixes]);

  // Load more sections when data changes
  useEffect(() => {
    if (sortedPrefixes.length > 0) {
      // Initially render first 5 sections for better infinite scrolling experience
      const initialSections = new Set<number>();
      for (let i = 0; i < Math.min(5, sortedPrefixes.length); i++) {
        initialSections.add(i);
      }
      setRenderedSections(initialSections);
    }
  }, [sortedPrefixes.length]);

  // Clean up observer when dependencies change or component unmounts
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [renderedSections.size, sortedPrefixes.length]);

  // Refresh completion states when translation data changes
  useEffect(() => {
    if (selectedProject?.id && firstLanguage && availableLanguages.length > 0) {
      // Clear cache to force recalculation
      languageStatsCache.current = null;
      
      // Refresh completion states for all languages
      availableLanguages.forEach(async (language) => {
        if (language !== firstLanguage) {
          try {
            const completionState = await batchTranslationService.verifyTranslationCompletion(
              selectedProject.id,
              language,
              firstLanguage
            );
            
            setCompletionStates(prev => {
              const newMap = new Map(prev);
              newMap.set(language, completionState);
              return newMap;
            });
          } catch (error) {
            // Error refreshing completion state
          }
        }
      });
    }
  }, [translationData, selectedProject?.id, firstLanguage, availableLanguages]);

  // Get columns for a prefix group
  const getColumnsForPrefix = useCallback((prefix: string) => {
    const prefixKeys = groupedTranslations[prefix]?.map(data => data.key.id) || [];
    const allPrefixKeysSelected = prefixKeys.length > 0 && prefixKeys.every(id => selectedRows.has(id));
    
    const columns = [
      { 
        header: prefix,
        key: 'key',
        editable: false, // Key column should not be editable
        isSelectable: true,
        isSelected: allPrefixKeysSelected,
        onHeaderSelect: () => {
          setSelectedRows(prev => {
            const newSet = new Set(prev);
            prefixKeys.forEach(id => {
              if (allPrefixKeysSelected) {
                newSet.delete(id);
              } else {
                newSet.add(id);
              }
            });
            return newSet;
          });
        }
      }
    ];

    // Only add one language column if source language is selected
    if (firstLanguage === selectedLanguage) {
      columns.push(
        { 
          header: getLanguageName(firstLanguage), 
          key: firstLanguage || 'first', 
          editable: true,
          isSelectable: false,
          isSelected: false,
          onHeaderSelect: () => {} 
        }
      );
    } else {
      columns.push(
        { 
          header: getLanguageName(firstLanguage), 
          key: firstLanguage || 'first', 
          editable: true,
          isSelectable: false,
          isSelected: false,
          onHeaderSelect: () => {} 
        },
        { 
          header: getLanguageName(selectedLanguage), 
          key: selectedLanguage || 'second', 
          editable: true,
          isSelectable: false,
          isSelected: false,
          onHeaderSelect: () => {} 
        }
      );
    }

    return columns;
  }, [groupedTranslations, selectedRows, firstLanguage, selectedLanguage, getLanguageName, setSelectedRows]);

  // Get cell content based on column
  const getCellContent = useCallback((row: TranslationData, columnKey: string) => {
    if (columnKey === 'key') {
      return row.key.key;
    }
    return row.strings.find(s => s.language_code === columnKey)?.value || '';
  }, []);



  const handleRowSelect = (rowKey: string, selected: boolean) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(rowKey);
      } else {
        newSet.delete(rowKey);
      }
      return newSet;
    });
  };

  const handleCreateReview = () => {
    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return;
    }
    setIsReviewDialogOpen(true);
  };

  const handleSaveReview = async (title: string, description: string) => {
    if (!selectedProject?.id || !user?.id) {
      return;
    }

    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return;
    }

    try {
      setIsCreatingReview(true);
      
      // Create the review with key IDs
      const { error: reviewError } = await supabase
        .from('Reviews')
        .insert([{
          title,
          description,
          project_id: selectedProject.id,
          owner_user_id: user.id,
          status: 'pending',
          created_at: new Date().toISOString(),
          last_updated_at: new Date().toISOString(),
          keys: Array.from(selectedRows) // Store the array of key IDs directly
        }])
        .select()
        .single();

      if (reviewError) {
        throw reviewError;
      }

      setIsReviewDialogOpen(false);
      setSelectedRows(new Set());
    } catch (err) {
      // console.error('Error creating review:', err);
    } finally {
      setIsCreatingReview(false);
    }
  };

  // Get selected key names
  const selectedKeyNames = useMemo(() => {
    return Array.from(selectedRows).map(keyId => {
      const translation = translationData.find((data: TranslationData) => data.key.id === keyId);
      return translation?.key.key || keyId;
    });
  }, [selectedRows, translationData]);

  const handleDeselectAll = () => {
    setSelectedRows(new Set());
  };

  // Handle cell change
  const handleCellChange = async (rowKey: string, columnKey: string, value: string) => {
    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return;
    }

    try {
      // Find the existing row
      const existingRowIndex = translationData.findIndex(item => getRowKey(item) === rowKey);
      if (existingRowIndex === -1) {
        return;
      }

      const existingRow = translationData[existingRowIndex];
      
      // For language columns, find or create the translation string
      const existingString = existingRow.strings.find(s => 
        s.language_code === columnKey
      );
      
      // Only proceed if there's an actual change in content
      if (existingString && existingString.value === value) {
        return;
      }
      
      // Set flag to indicate manual edit is in progress
      window.sessionStorage.setItem('texta_manual_edit', 'true');
      
      // Always update the database and get the returned data
      const updatedString = await upsertTranslationString(rowKey, columnKey, value, selectedProject?.id || '');
      
      // Show a simple notification that the text was updated
      showNotification('Saved');
      
      // Update local state using the actual returned data from the database
      const updatedStrings = [...existingRow.strings];
      const existingStringIndex = existingRow.strings.findIndex(s => s.language_code === columnKey);
      
      if (existingStringIndex >= 0) {
        // Update existing translation string
        updatedStrings[existingStringIndex] = updatedString;
      } else {
        // Add new translation string
        updatedStrings.push(updatedString);
      }
      
      const updatedData = [...translationData];
      updatedData[existingRowIndex] = {
        ...existingRow,
        strings: updatedStrings
      };
      
      setTranslationData(updatedData);
      
      // Invalidate cache since data has changed
      if (selectedProject) {
        translationDataCache.current.delete(selectedProject.id);
      }
      
      // Perform immediate completion state update for cell content changes
      if (columnKey !== firstLanguage) {
        updateCompletionStateForCellChange(columnKey, value, existingString?.value || '');
        
        // If cell was completely emptied, trigger immediate verification for better UX
        if (!value || value.trim() === '') {
          // Clear the debounced timer and trigger immediate refresh for this language only
          if (debouncedRefreshCompletionStates.current) {
            clearTimeout(debouncedRefreshCompletionStates.current);
          }
          // Use a small delay to allow the immediate update to settle
          setTimeout(() => {
            refreshCompletionStateForLanguage(columnKey);
          }, 100);
        }
      }
      
      // Trigger debounced refresh of completion states for full verification
      triggerDebouncedRefresh();
      
      // Clear the manual edit flag after a short delay
      setTimeout(() => {
        window.sessionStorage.removeItem('texta_manual_edit');
      }, 1000);
    } catch (error) {
      // console.error('Error updating cell:', error);
      showNotification('Failed to update translation');
      // Clear the manual edit flag on error
      window.sessionStorage.removeItem('texta_manual_edit');
    }
  };

  // Handle cell blur
  const handleCellBlur = () => {
    // Don't blur if we're in the process of translating
    if (window.sessionStorage.getItem('texta_translating')) {
      return;
    }
    
    // Use a small delay to allow action bar interactions
    setTimeout(() => {
      // Double-check we're not translating before blurring
      if (!window.sessionStorage.getItem('texta_translating')) {
        setIsInputFocused(false);
        setFocusedCellInfo(null);
      }
    }, 200);
  };

  // Handle cross-section tab navigation
  const handleTabNavigation = (direction: 'next' | 'previous', rowIndex: number, columnIndex: number) => {
    // Find the current section based on where focus actually is
    const actualCurrentSectionIndex = findCurrentSectionIndex();
    
    if (direction === 'next') {
      // Moving to next section
      if (actualCurrentSectionIndex < sortedPrefixes.length - 1) {
        const nextSectionIndex = actualCurrentSectionIndex + 1;
        setCurrentSectionIndex(nextSectionIndex);
        
        // Focus first cell of next section
        setTimeout(() => {
          const nextPrefix = sortedPrefixes[nextSectionIndex];
          const nextSectionRef = sectionRefs.current[nextPrefix];
          if (nextSectionRef?.current) {
            const firstCell = nextSectionRef.current.querySelector('.cell-content[contenteditable="true"]') as HTMLElement;
            if (firstCell) {
              firstCell.focus();
            }
          }
        }, 0);
      }
    } else {
      // Moving to previous section
      if (actualCurrentSectionIndex > 0) {
        const prevSectionIndex = actualCurrentSectionIndex - 1;
        setCurrentSectionIndex(prevSectionIndex);
        
        // Focus last cell of previous section
        setTimeout(() => {
          const prevPrefix = sortedPrefixes[prevSectionIndex];
          const prevSectionRef = sectionRefs.current[prevPrefix];
          if (prevSectionRef?.current) {
            const cells = prevSectionRef.current.querySelectorAll('.cell-content[contenteditable="true"]');
            const lastCell = cells[cells.length - 1] as HTMLElement;
            if (lastCell) {
              lastCell.focus();
            }
          }
        }, 0);
      }
    }
  };

  // Handle creating a new translation key
  const handleCreateKey = async (keyName: string, defaultValue: string) => {
    if (!selectedProject || !firstLanguage) return;

    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return;
    }
    
    try {
      setIsCreatingKey(true);
      
      // Check if key already exists
      const keyExists = await checkKeyExists(selectedProject.id, keyName);
      if (keyExists) {
        throw new Error('A key with this name already exists in this project');
      }
      
      // Create the key
      const newKey = await createTranslationKey(selectedProject.id, keyName);
      
      // If there's a default value, create the translation string
      if (defaultValue && firstLanguage) {
        await upsertTranslationString(newKey.id, firstLanguage, defaultValue, selectedProject.id);
      }
      
      // Create the new translation data entry
      const newTranslationData = {
        key: newKey,
        strings: defaultValue ? [{
          id: 'temp',
          value: defaultValue,
          language_code: firstLanguage,
          key_id: newKey.id,
          created_at: new Date().toISOString()
        }] : []
      };

      // Add the new key to the state, maintaining alphabetical order
      setTranslationData(prev => {
        const updated = [...prev, newTranslationData];
        return updated.sort((a, b) => a.key.key.localeCompare(b.key.key));
      });
      
      // Invalidate cache since data has changed
      if (selectedProject) {
        translationDataCache.current.delete(selectedProject.id);
      }
      
      // Refresh completion states to reflect the new key
      await refreshCompletionStates();
      
      showNotification('Key added');
      
      // Close the dialog
      setIsKeyDialogOpen(false);
    } catch (err) {
      // console.error('Error creating translation key:', err);
      showNotification('Failed to add key');
      // Re-throw the error to be handled by the dialog
      throw err;
    } finally {
      setIsCreatingKey(false);
    }
  };

  // Handle edit button click
  const handleEditClick = (keyId: string) => {
    const keyData = translationData.find(data => data.key.id === keyId);
    if (keyData) {
      setKeyToEdit(keyData.key);
      setIsEditDialogOpen(true);
    }
  };

  // Handle saving edited key name
  const handleSaveKeyEdit = async (newKeyName: string) => {
    if (!keyToEdit) return;

    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return;
    }
    
    try {
      setIsEditingKey(true);
      
      // Update the key name
      const updatedKey = await updateTranslationKey(keyToEdit.id, newKeyName);
      
      // Update the state with the new key name
      setTranslationData(prev => prev.map(data => 
        data.key.id === keyToEdit.id ? { ...data, key: updatedKey } : data
      ));
      
      // Invalidate cache since data has changed
      if (selectedProject) {
        translationDataCache.current.delete(selectedProject.id);
      }
      
      // Refresh completion states to reflect the key name change
      await refreshCompletionStates();
    } catch (err) {
      // console.error('Error updating key name:', err);
      throw err;
    } finally {
      setIsEditingKey(false);
    }
  };

  // Handle deleting a key
  const handleDeleteKey = async () => {
    if (!keyToEdit) return;
    
    try {
      // Delete the key
      await deleteTranslationKey(keyToEdit.id);
      
      // Update the state to remove the deleted key
      setTranslationData(prev => prev.filter(data => data.key.id !== keyToEdit.id));
      
      // Invalidate cache since data has changed
      if (selectedProject) {
        translationDataCache.current.delete(selectedProject.id);
      }
      
      // Refresh completion states to reflect the deleted key
      await refreshCompletionStates();
      
      showNotification('Key deleted');
    } catch (err) {
      // console.error('Error deleting translation key:', err);
      showNotification('Failed to delete key');
      throw err;
    }
  };

  const handleAddToReview = () => {
    setIsSelectReviewDialogOpen(true);
  };

  const handleReviewSelect = async (reviewId: string) => {
    if (!selectedProject?.id) return;
    
    try {
      setIsAddingToReview(true);
      
      // Get the current review
      const { data: review, error: reviewError } = await supabase
        .from('Reviews')
        .select('keys')
        .eq('id', reviewId)
        .single();

      if (reviewError) throw reviewError;

      // Get current keys array or initialize empty array
      const currentKeys = Array.isArray(review?.keys) ? review.keys : [];
      
      // Add new keys, avoiding duplicates
      const newKeys = Array.from(selectedRows);
      const uniqueKeys = Array.from(new Set([...currentKeys, ...newKeys]));

      // Update the review with the new keys
      const { error: updateError } = await supabase
        .from('Reviews')
        .update({ keys: uniqueKeys })
        .eq('id', reviewId);

      if (updateError) throw updateError;

      // Clear selection
      setSelectedRows(new Set());
    } catch (err) {
      // console.error('Error adding keys to review:', err);
      throw err;
    } finally {
      setIsAddingToReview(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- toolbar button handlers
  const handleOpenKeyDialogBtn = () => {
    setIsKeyDialogOpen(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- toolbar import button
  const handleOpenImportDialogBtn = () => {
    setIsImportDialogOpen(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- toolbar export button
  const handleOpenExportDialog = () => {
    setIsExportDialogOpen(true);
  };

  const handleImportComplete = (stats: {
    addedLanguages: string[];
    addedKeys: number;
    addedStrings: number;
    skippedKeys: number;
    skippedStrings: number;
    errors: string[];
  }) => {
    // Show notification with import results
    const successMessage = `
      Import complete:
      ${stats.addedKeys} keys added
      ${stats.addedStrings} translations added
      ${stats.addedLanguages.length > 0 ? `${stats.addedLanguages.length} languages added` : ''}
    `;
    
    showNotification(stats.errors.length > 0 
      ? `Import completed with ${stats.errors.length} errors. ${successMessage}`
      : successMessage
    );
    
    // Refresh the translations
    loadTranslations();
  };

  // Function to get row key
  const getRowKey = (row: TranslationData) => row.key.id;

  // Create stable callback functions to prevent TranslationTableSection re-renders
  const stableGetRowKey = useCallback((row: TranslationData) => {
    return getRowKey(row);
  }, []);

  const stableGetCellContent = useCallback((row: TranslationData, columnKey: string) => {
    return getCellContent(row, columnKey);
  }, [getCellContent]);

  const stableGetColumnsForPrefix = useCallback((prefix: string) => {
    return getColumnsForPrefix(prefix);
  }, [getColumnsForPrefix]);

  // Handle cell focus
  const handleCellFocus = (rowKey: string, columnKey: string, content: string, sourceLanguage: string | null) => {
    let sourceText = '';
    
    // Find the source text for translation
    if (sourceLanguage) {
      const allTranslations = Object.values(groupedTranslations).flat();
      const rowData = allTranslations.find(item => getRowKey(item) === rowKey);
      
      if (rowData) {
        // Get content from the first language (source language), not the key
        // First try to get the source text from the first language
        if (firstLanguage) {
          sourceText = getCellContent(rowData, firstLanguage);
        }
        
        // If no translation in first language, use the key as fallback
        if (!sourceText && sourceLanguage === 'key') {
          sourceText = getCellContent(rowData, 'key');
        }
      }
    }
    
    setFocusedCellInfo({
      rowKey,
      columnKey,
      content,
      sourceLanguage,
      sourceText
    });
    
    setIsInputFocused(true);
  };

  // Handle translate
  const handleTableTranslate = async (rowKey: string, columnKey: string, translatedText: string) => {
    if (!translatedText || !translatedText.trim()) {
      return;
    }
    
    // Find the row data
    const allTranslations = Object.values(groupedTranslations).flat();
    const rowData = allTranslations.find(item => getRowKey(item) === rowKey);
    
    if (rowData) {
      // Find and update the cell in the DOM - use simplified cell key (just rowKey)
      const cellKey = rowKey;
      
      // Try different selectors to find the cell
      let cellElement: Element | null = null;
      
      // Use safer selectors that won't throw errors
      const safeSelectors = [
        `.editable-table-cell[data-row="${rowKey}"][data-col="${columnKey}"]`,
        `[data-cell-key="${cellKey}"]`,
        `#${cellKey}`,
        `.editable-table-cell[contenteditable="true"]`
      ];
      
      // Try each selector
      for (const selector of safeSelectors) {
        try {
          const element = document.querySelector(selector);
          if (element) {
            cellElement = element;
            break;
          }
        } catch (err) {
        }
      }
      
      if (!cellElement) {
        // Try a broader search as a fallback
        const allCells = document.querySelectorAll('.editable-table-cell');
        
        allCells.forEach(cell => {
          if (cell.textContent && cell.textContent.trim() === '') {
            cellElement = cell;
          }
        });
      }
      
      if (cellElement) {
        // Add translating class to show the magic border
        cellElement.classList.add('translating');
        
        // Set initial empty content before animation
        if (cellElement.getAttribute('contenteditable') === 'true') {
          cellElement.textContent = '';
        }
        
        // Create a unique animation effect using inline style with more pronounced animation
        const animateWords = (text: string) => {
          // Split by spaces but preserve spaces in the output
          const words = text.split(/\s+/);
          return words.map((word: string, index: number) => {
            // Calculate staggered delay based on word position
            const delay = 0.08 * index;
            // Make animation more dramatic for longer words
            const distance = Math.min(10 + word.length, 20);
            
            return `<span 
              class="word-animate" 
              style="
                animation-delay: ${delay}s; 
                animation-duration: 0.6s;
                display: inline-block; 
                opacity: 0; 
                transform: translateY(${distance}px);
                animation: fadeInUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) ${delay}s forwards;
                position: relative;
              "
            >${word}</span>`;
          }).join(' ');
        };
        
        const animatedHTML = animateWords(translatedText);
        
        // Apply animated HTML immediately
        if (cellElement.getAttribute('contenteditable') === 'true') {
          cellElement.innerHTML = animatedHTML;
        }
        
        // For non-contenteditable elements, find and update child elements
        const cellContent = cellElement.querySelector('.cell-content');
        if (cellContent) {
          cellContent.innerHTML = animatedHTML;
        }
        
        // After the animation completes, set the actual text content for editing
        const animationDuration = Math.min(translatedText.split(/\s+/).length * 50 + 1500, 3500); // Cap at 3.5 seconds for long text
        
        // Create a reference to the element to use in the timeout
        const elementForTimeout = cellElement;
        
        setTimeout(() => {
          // Update with plain text after animation finishes
          if (elementForTimeout && elementForTimeout.getAttribute('contenteditable') === 'true') {
            elementForTimeout.textContent = translatedText;
            
            // Save changes to the data model
            handleCellChange(rowKey, columnKey, translatedText);
          }
          
          // Update spans as well
          if (elementForTimeout) {
            const spans = elementForTimeout.querySelectorAll('.cell-content > span:not(.word-animate)');
            spans.forEach(span => {
              span.textContent = translatedText;
            });
            
            // Also update the cell-content's text if there are no spans
            if (spans.length === 0) {
              const cellContent = elementForTimeout.querySelector('.cell-content');
              if (cellContent) {
                cellContent.textContent = translatedText;
                // Add placeholder highlighting after setting the text
                setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
              }
            }
          }
        }, animationDuration);
        
        // Store a reference to the element for the timeout
        const elementRef = cellElement;
        
        // Keep the animation visible for a bit, but not too long
        setTimeout(() => {
          if (elementRef) {
            elementRef.classList.remove('translating');
            
            // Ensure the text is still there after animation
            const contentEditableElement = elementRef.getAttribute('contenteditable') === 'true' ? 
              elementRef : 
              elementRef.querySelector('[contenteditable="true"]');
              
            if (contentEditableElement && !contentEditableElement.textContent) {
              contentEditableElement.textContent = translatedText;
            }
            
            // Check cell-content as well and add placeholder highlighting
            const cellContent = elementRef.querySelector('.cell-content');
            if (cellContent) {
              if (!cellContent.textContent) {
                cellContent.textContent = translatedText;
              }
              // Add placeholder highlighting after animation
              setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
            }
          }
        }, animationDuration + 1000);
        
        // Update the data model and database immediately
        await handleCellChange(rowKey, columnKey, translatedText);
        
        // Force a refresh of the UI to ensure consistency
        setTimeout(() => {
          // Update the translation data to trigger a re-render
          const updatedData = [...translationData];
          
          // Find and update the specific row/column
          const rowIndex = updatedData.findIndex(item => getRowKey(item) === rowKey);
          if (rowIndex !== -1) {
            const stringIndex = updatedData[rowIndex].strings.findIndex(s => s.language_code === columnKey);
            
            // If string exists, update it; otherwise create a new one
            if (stringIndex !== -1) {
              updatedData[rowIndex].strings[stringIndex].value = translatedText;
            } else if (columnKey !== 'key') {
              // Add a new string if it doesn't exist
              updatedData[rowIndex].strings.push({
                id: `temp-${Date.now()}`, // Temporary ID
                key_id: rowKey,
                language_code: columnKey,
                value: translatedText,
                created_at: new Date().toISOString()
              });
            }
          }
          
          setTranslationData(updatedData);
          
          // Add placeholder highlighting after state update
          const cellContent = elementRef?.querySelector('.cell-content');
          if (cellContent) {
            setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
          }
        }, animationDuration + 200);
      } else {
        // Even if we can't find the cell, still update the data
        await handleCellChange(rowKey, columnKey, translatedText);
      }
    } else {
      // Could not find row data for key
    }
  };

  // Function to get missing translations count for the selected language
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for UI
  const getMissingTranslationsCount = () => {
    return findEmptyCellsForTranslation().length;
  };

  // Function to get missing source content count
  const getMissingSourceContentCount = useCallback(() => {
    if (!firstLanguage) return 0;
    
    const allTranslations = Object.values(groupedTranslations).flat();
    let missingSourceCount = 0;
    
    allTranslations.forEach(row => {
      const sourceString = row.strings.find(str => str.language_code === firstLanguage);
      if (!sourceString || !sourceString.value.trim()) {
        missingSourceCount++;
      }
    });
    
    return missingSourceCount;
  }, [firstLanguage, groupedTranslations]);

  // Perform immediate completion state update for cell content changes
  const updateCompletionStateForCellChange = (languageCode: string, newValue: string, oldValue: string) => {
    if (!selectedProject || !firstLanguage || languageCode === firstLanguage) return;
    
    const currentState = completionStates.get(languageCode);
    
    // Determine if this change affects completion state
    const wasEmpty = !oldValue || oldValue.trim() === '';
    const isEmpty = !newValue || newValue.trim() === '';
    
    // Only update if the completion state actually changes
    if (wasEmpty !== isEmpty) {
      if (currentState) {
        // Update existing state
        const newTranslatedKeys = isEmpty 
          ? Math.max(0, currentState.translatedKeys - 1)
          : currentState.translatedKeys + 1;
        const newMissingKeys = isEmpty 
          ? currentState.missingKeys + 1
          : Math.max(0, currentState.missingKeys - 1);
        
        const updatedState = {
          ...currentState,
          translatedKeys: newTranslatedKeys,
          missingKeys: newMissingKeys,
          isComplete: newMissingKeys === 0
        };
        
        setCompletionStates(prev => {
          const newMap = new Map(prev);
          newMap.set(languageCode, updatedState);
          return newMap;
        });
      } else {
        // If no current state exists, trigger a refresh for this language only
        refreshCompletionStateForLanguage(languageCode);
        return;
      }
      
      // Clear the cache to force recalculation
      languageStatsCache.current = null;
    }
  };

  // Helper function to refresh completion states for all languages
  const refreshCompletionStates = async () => {
    if (!selectedProject || !firstLanguage || !availableLanguages) return;
    
    const newCompletionStates = new Map(completionStates);
    
    for (const language of availableLanguages) {
      if (language !== firstLanguage) {
        try {
          const completionState = await batchTranslationService.verifyTranslationCompletion(
            selectedProject.id,
            language,
            firstLanguage
          );
          newCompletionStates.set(language, completionState);
        } catch (error) {
          // Error refreshing completion state
        }
      }
    }
    
    setCompletionStates(newCompletionStates);
  };

  // Helper function to refresh completion state for a specific language only
  const refreshCompletionStateForLanguage = async (languageCode: string) => {
    if (!selectedProject || !firstLanguage || languageCode === firstLanguage) return;
    
    try {
      const completionState = await batchTranslationService.verifyTranslationCompletion(
        selectedProject.id,
        languageCode,
        firstLanguage
      );
      
      setCompletionStates(prev => {
        const newMap = new Map(prev);
        newMap.set(languageCode, completionState);
        return newMap;
      });
      
      // Clear the cache to force recalculation
      languageStatsCache.current = null;
    } catch (error) {
      // Error refreshing completion state
    }
  };

  // Debounced refresh for completion states (used for frequent updates like cell changes)
  const debouncedRefreshCompletionStates = useRef<NodeJS.Timeout | null>(null);
  const triggerDebouncedRefresh = () => {
    if (debouncedRefreshCompletionStates.current) {
      clearTimeout(debouncedRefreshCompletionStates.current);
    }
    debouncedRefreshCompletionStates.current = setTimeout(() => {
      refreshCompletionStates();
    }, 1000); // Reduced to 1 second for faster response
  };

  // Function to get missing translations count for all languages
  const getMissingTranslationsForAllLanguages = useMemo(() => {
    if (!firstLanguage || !availableLanguages) return [];
    
    // Create cache key that includes completion state values
    const completionStateHash = Array.from(completionStates.entries())
      .map(([lang, state]) => `${lang}:${state.isComplete ? '1' : '0'}:${state.missingKeys}:${state.translatedKeys}`)
      .join('|');
    const cacheKey = `${firstLanguage}-${availableLanguages.join(',')}-${groupedTranslations ? Object.keys(groupedTranslations).length : 0}-${completionStateHash}`;
    
    // Check cache first
    if (languageStatsCache.current && languageStatsCache.current.key === cacheKey) {
      return languageStatsCache.current.stats;
    }
    
    const allTranslations = Object.values(groupedTranslations).flat();
    const languageStats: Array<{
      language: string;
      languageName: string;
      missingCount: number;
      totalKeys: number;
      canTranslate: boolean;
      completionState?: any;
    }> = [];

    // Process languages in the exact order they appear in availableLanguages
    availableLanguages.forEach(language => {
      let missingCount = 0;
      let totalKeys = 0;
      let completionState = null;
      
      if (language === firstLanguage) {
        // Source language - count all keys that need content
        missingCount = getMissingSourceContentCount();
        totalKeys = allTranslations.length;
      } else {
        // Target languages - only count keys that have source content
        completionState = completionStates.get(language);
        
        if (completionState) {
          // Use verified completion state
          missingCount = Math.max(0, completionState.missingKeys);
          totalKeys = completionState.totalKeys; // This is now keysWithSourceContent
        } else {
          // Fallback to calculated state - only count keys with source content
          let keysWithSourceContent = 0;
          allTranslations.forEach(row => {
            // Check if this row has valid source text
            const sourceString = row.strings.find(str => str.language_code === firstLanguage);
            if (sourceString && sourceString.value.trim()) {
              keysWithSourceContent++;
              
              // Check if target translation exists and is not empty
              const targetString = row.strings.find(str => str.language_code === language);
              if (!targetString || !targetString.value.trim()) {
                missingCount++;
              }
            }
          });
          totalKeys = keysWithSourceContent;
        }
      }
      
      // Add language stats in the same order as availableLanguages
      const finalMissingCount = completionState?.isComplete ? 0 : Math.max(0, missingCount);
      const canTranslate = language !== firstLanguage && totalKeys > 0 && missingCount > 0 && !completionState?.isComplete;
      

      
      languageStats.push({
        language,
        languageName: getLanguageName(language),
        missingCount: finalMissingCount,
        totalKeys: Math.max(0, totalKeys),
        canTranslate,
        completionState
      });
      

    });

    // Cache the result
    languageStatsCache.current = {
      key: cacheKey,
      stats: languageStats
    };

    return languageStats;
  }, [firstLanguage, availableLanguages, groupedTranslations, completionStates, getMissingSourceContentCount, getLanguageName]);

  // Add a new function to find empty cells in the selected language
  const findEmptyCellsForTranslation = () => {
    if (!selectedLanguage) return [];
    
    const emptyCells = [];
    const allTranslations = Object.values(groupedTranslations).flat();

    // Check each row for empty cells in the selected language
    for (const row of allTranslations) {
      const rowKey = getRowKey(row);
      
      // First make sure we have a source text to translate from
      let sourceText = '';
      
      // Try to get source text from the first language
      if (firstLanguage) {
        const sourceString = row.strings.find(str => str.language_code === firstLanguage);
        if (sourceString && sourceString.value.trim()) {
          sourceText = sourceString.value;
        }
      }
      
      // Only proceed if we have valid source text to translate from
      if (sourceText && sourceText.trim() && firstLanguage) {
        // Get existing translation for this row and language
        const existingTranslation = row.strings.find(str => str.language_code === selectedLanguage);
        
        // If no translation exists or it's empty, add to the list of cells to translate
        if (!existingTranslation || !existingTranslation.value.trim()) {
          emptyCells.push({
            rowKey,
            columnKey: selectedLanguage,
            sourceText,
            sourceLanguage: firstLanguage
          });
        }
      }
    }
    
    return emptyCells;
  };

  // Function to handle translation for a specific language
  const handleTranslateLanguage = async (targetLanguage: string) => {
    if (!firstLanguage || !selectedProject || !selectedTeam?.id) {
      showNotification("Missing required data for translation");
      return;
    }

    // Set preparing state immediately
    setPreparingLanguages(prev => new Set(prev).add(targetLanguage));

    // First, refresh the completion state to get the most up-to-date missing translations count
    if (selectedProject?.id && firstLanguage) {
      try {
        const freshCompletionState = await batchTranslationService.verifyTranslationCompletion(
          selectedProject.id,
          targetLanguage,
          firstLanguage
        );
        
        // Update completion states with fresh data
        setCompletionStates(prev => {
          const newMap = new Map(prev);
          newMap.set(targetLanguage, freshCompletionState);
          return newMap;
        });
        
        // If the language is already complete, don't proceed with translation
        if (freshCompletionState.isComplete) {
          showNotification(`${getLanguageName(targetLanguage)} is already complete`);
          setPreparingLanguages(prev => {
            const newSet = new Set(prev);
            newSet.delete(targetLanguage);
            return newSet;
          });
          return;
        }
      } catch (error) {
        // Continue with translation even if verification fails
      }
    }

    // Refresh translation data to ensure we have the latest state
    try {
      const freshTranslationData = await fetchAllTranslationData(selectedProject.id);
      setTranslationData(freshTranslationData);
      
      // Clear the translation grouper cache to ensure fresh grouping
      translationGrouper.clearCache();
    } catch (error) {
      // Continue with existing data if refresh fails
    }

    // Find all empty cells for this specific language using fresh data
    const cellsToTranslate = findEmptyCellsForSpecificLanguage(targetLanguage);
    
    if (cellsToTranslate.length === 0) {
      showNotification(`No empty translations found for ${getLanguageName(targetLanguage)}`);
      // Remove preparing state if no cells to translate
      setPreparingLanguages(prev => {
        const newSet = new Set(prev);
        newSet.delete(targetLanguage);
        return newSet;
      });
      return;
    }
    
    showNotification(`Starting batch translation of ${cellsToTranslate.length} keys to ${getLanguageName(targetLanguage)}...`);
    
    // Clear the language stats cache to force recalculation with fresh data
    languageStatsCache.current = null;
    
    try {
      // Prepare batch translation request
      const allTranslations = Object.values(groupedTranslations).flat();
      const requestItems = cellsToTranslate.map(cell => {
        const rowData = allTranslations.find(item => getRowKey(item) === cell.rowKey);
        return {
          key_id: rowData?.key?.id || '',
          source_text: cell.sourceText,
          source_language: cell.sourceLanguage,
          target_language: cell.columnKey
        };
      }).filter(item => {
        // Additional validation to ensure we have valid data
        const isValid = item.key_id && 
                       item.source_text && 
                       item.source_text.trim() && 
                       item.source_language && 
                       item.target_language;
        
        if (!isValid) {
          // Filtering out invalid item
        }
        
        return isValid;
      });

      const batchRequest: BatchTranslationRequest = {
        items: requestItems,
        project_id: selectedProject.id,
        team_id: selectedTeam?.id
      };



      // Add translating class to all cells
      cellsToTranslate.forEach(cell => {
        const cellElement = document.querySelector(`.editable-table-cell[data-row="${cell.rowKey}"][data-col="${cell.columnKey}"]`);
        if (cellElement) {
          cellElement.classList.add('translating');
        }
      });

      // Start batch translation on backend
      const batchId = await batchTranslationService.startBatchTranslation(batchRequest);
      
      // Update active language jobs with initial 0% progress
      setActiveLanguageJobs(prev => {
        const newMap = new Map(prev);
        newMap.set(targetLanguage, { 
          jobId: batchId, 
          progress: {
            id: batchId,
            progress_percentage: 0,
            completed_items: 0,
            total_items: cellsToTranslate.length,
            status: 'pending'
          }
        });
        return newMap;
      });

      // Remove preparing state now that we have an active job
      setPreparingLanguages(prev => {
        const newSet = new Set(prev);
        newSet.delete(targetLanguage);
        return newSet;
      });

      // Subscribe to progress updates
      batchTranslationService.subscribeToProgress(batchId, {
        onProgress: (progress) => {
          setActiveLanguageJobs(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(targetLanguage);
            if (existing) {
              newMap.set(targetLanguage, { ...existing, progress });
            }
            return newMap;
          });
        },
        
        onComplete: async (progress) => {
          // Remove from active jobs
          setActiveLanguageJobs(prev => {
            const newMap = new Map(prev);
            newMap.delete(targetLanguage);
            return newMap;
          });
          
          // Remove translating class from all cells
          cellsToTranslate.forEach(cell => {
            const cellElement = document.querySelector(`.editable-table-cell[data-row="${cell.rowKey}"][data-col="${cell.columnKey}"]`);
            if (cellElement) {
              cellElement.classList.remove('translating');
            }
          });
          
          // Verify actual completion state from backend
          if (selectedProject?.id && firstLanguage) {
            try {
              const completionState = await batchTranslationService.verifyTranslationCompletion(
                selectedProject.id,
                targetLanguage,
                firstLanguage
              );
              
              // Update completion states
              setCompletionStates(prev => {
                const newMap = new Map(prev);
                newMap.set(targetLanguage, completionState);
                return newMap;
              });
              
              // Set up periodic verification for this language
              const cleanup = batchTranslationService.setupCompletionVerification(
                selectedProject.id,
                targetLanguage,
                firstLanguage,
                (newState) => {
                  setCompletionStates(prev => {
                    const newMap = new Map(prev);
                    newMap.set(targetLanguage, newState);
                    return newMap;
                  });
                }
              );
              
              // Store cleanup function
              setVerificationCleanups(prev => {
                const newMap = new Map(prev);
                newMap.set(targetLanguage, cleanup);
                return newMap;
              });
              
              // Mark as completed only if actually complete
              if (completionState.isComplete) {
                setCompletedTranslations(prev => new Set(Array.from(prev).concat(targetLanguage)));
              }
              
            } catch (error) {
              // Error verifying completion state
            }
          }
          
          // If user is on the tab that just finished translating, refresh everything at once
            if (selectedLanguage === targetLanguage) {
              // Batch all updates together
              Promise.all([
                // Get fresh table data
                fetchAllTranslationData(selectedProject.id),
                // Recalculate completion state
                selectedProject?.id && firstLanguage 
                  ? batchTranslationService.verifyTranslationCompletion(
                      selectedProject.id,
                      targetLanguage,
                      firstLanguage
                    )
                  : Promise.resolve(null)
              ]).then(([freshTranslations, updatedCompletionState]) => {
                // Update all state in a single React cycle
                React.startTransition(() => {
                  // Clear cache and update table data
                  translationGrouper.clearCache();
                  setTranslationData(freshTranslations);
                  
                  // Update completion state if we got it
                  if (updatedCompletionState) {
                    setCompletionStates(prev => {
                      const newMap = new Map(prev);
                      newMap.set(targetLanguage, updatedCompletionState);
                      return newMap;
                    });
                  }
                });
              });
            }
          
          if (progress.failed_items > 0) {
            showNotification(`✅ ${getLanguageName(targetLanguage)} translation complete! ${progress.completed_items} successful, ${progress.failed_items} failed`);
          } else {
            showNotification(`✅ ${getLanguageName(targetLanguage)} translation complete! ${progress.completed_items} keys successfully translated`);
          }
        },
        
        onError: (error) => {
          // Remove from active jobs
          setActiveLanguageJobs(prev => {
            const newMap = new Map(prev);
            newMap.delete(targetLanguage);
            return newMap;
          });
          
          // Remove translating class from all cells
          cellsToTranslate.forEach(cell => {
            const cellElement = document.querySelector(`.editable-table-cell[data-row="${cell.rowKey}"][data-col="${cell.columnKey}"]`);
            if (cellElement) {
              cellElement.classList.remove('translating');
            }
          });
          
          showNotification(`❌ ${getLanguageName(targetLanguage)} translation failed: ${error}`);
        }
      });

    } catch (error) {
      // Remove translating class from all cells
      cellsToTranslate.forEach(cell => {
        const cellElement = document.querySelector(`.editable-table-cell[data-row="${cell.rowKey}"][data-col="${cell.columnKey}"]`);
        if (cellElement) {
          cellElement.classList.remove('translating');
        }
      });
      
      showNotification(`❌ Failed to start ${getLanguageName(targetLanguage)} translation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Function to find empty cells for a specific language
  const findEmptyCellsForSpecificLanguage = (targetLanguage: string) => {
    if (!firstLanguage) return [];
    
    const emptyCells = [];
    // Use the most current translation data
    const allTranslations = Object.values(groupedTranslations).flat();

    for (const row of allTranslations) {
      const rowKey = getRowKey(row);
      
      // First make sure we have a source text to translate from
      const sourceString = row.strings.find(str => str.language_code === firstLanguage);
      if (sourceString && sourceString.value.trim()) {
        // Get existing translation for this row and target language
        const existingTranslation = row.strings.find(str => str.language_code === targetLanguage);
        
        // If no translation exists or it's empty, add to the list of cells to translate
        if (!existingTranslation || !existingTranslation.value.trim()) {
          emptyCells.push({
            rowKey,
            columnKey: targetLanguage,
            sourceText: sourceString.value,
            sourceLanguage: firstLanguage
          });
        }
      }
    }
    
    return emptyCells;
  };

  // Function to cancel translation for a specific language
  const handleCancelLanguageTranslation = async (targetLanguage: string) => {
    const activeJob = activeLanguageJobs.get(targetLanguage);
    const isPreparing = preparingLanguages.has(targetLanguage);

    try {
      // If there's an active job, cancel it on the backend
      if (activeJob) {
        await batchTranslationService.cancelBatchTranslation(activeJob.jobId);
        
        // Remove from active jobs
        setActiveLanguageJobs(prev => {
          const newMap = new Map(prev);
          newMap.delete(targetLanguage);
          return newMap;
        });
      }

      // Remove from preparing state if preparing
      if (isPreparing) {
        setPreparingLanguages(prev => {
          const newSet = new Set(prev);
          newSet.delete(targetLanguage);
          return newSet;
        });
      }

      // Remove translating class from all cells for this language
      const allCells = document.querySelectorAll(`.editable-table-cell[data-col="${targetLanguage}"]`);
      allCells.forEach(cell => {
        cell.classList.remove('translating');
      });

      // Refresh completion state to update missing translations count
      if (selectedProject?.id && firstLanguage) {
        try {
          const completionState = await batchTranslationService.verifyTranslationCompletion(
            selectedProject.id,
            targetLanguage,
            firstLanguage
          );
          
          setCompletionStates(prev => {
            const newMap = new Map(prev);
            newMap.set(targetLanguage, completionState);
            return newMap;
          });
        } catch (error) {
          // Error refreshing completion state after cancel
        }
      }
      
      showNotification(`🛑 ${getLanguageName(targetLanguage)} translation cancelled`);
    } catch (error) {
      showNotification(`❌ Failed to cancel ${getLanguageName(targetLanguage)} translation`);
    }
  };

  // Function to manually verify completion state for debugging
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- verify completion handler
  const handleVerifyCompletion = async (targetLanguage: string) => {
    if (!selectedProject?.id || !firstLanguage) return;
    
    try {
      const completionState = await batchTranslationService.verifyTranslationCompletion(
        selectedProject.id,
        targetLanguage,
        firstLanguage
      );
      
      // Update completion states
      setCompletionStates(prev => {
        const newMap = new Map(prev);
        newMap.set(targetLanguage, completionState);
        return newMap;
      });
      
      showNotification(`Verification complete for ${getLanguageName(targetLanguage)}`);
    } catch (error) {
      showNotification(`Error verifying ${getLanguageName(targetLanguage)}`);
    }
  };

  // Function to delete all translations for a specific language
  const handleDeleteLanguageTranslations = async (targetLanguage: string) => {
    if (!selectedProject?.id) {
      showNotification("No project selected");
      return;
    }

    try {
      // Get all translation strings for this language in the project
      const { data: translationStrings, error } = await supabase
        .from('TranslationStrings')
        .select(`
          id,
          TranslationKeys!inner (
            project_id
          )
        `)
        .eq('language_code', targetLanguage)
        .eq('TranslationKeys.project_id', selectedProject.id);

      if (error) {
        throw error;
      }

      if (!translationStrings || translationStrings.length === 0) {
        showNotification(`No translations found for ${getLanguageName(targetLanguage)}`);
        return;
      }

      // Delete all translation strings for this language
      const stringIds = translationStrings.map(ts => ts.id);
      const { error: deleteError } = await supabase
        .from('TranslationStrings')
        .delete()
        .in('id', stringIds);

      if (deleteError) {
        throw deleteError;
      }

      // Remove from completed translations set
      setCompletedTranslations(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(targetLanguage);
        return newSet;
      });

      // Reload translations to reflect changes
      loadTranslations();
      
      showNotification(`🗑️ All ${getLanguageName(targetLanguage)} translations deleted`);
    } catch (error) {
      showNotification(`❌ Failed to delete ${getLanguageName(targetLanguage)} translations`);
    }
  };



  // Add the handler function for individual cell translation
  const handleCellTranslate = async (rowKey: string, columnKey: string, sourceText: string) => {
    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return null;
    }

    try {
      // Configure the source language in the service if needed
      if (firstLanguage) {
        aiTranslationService.setSourceLanguage(firstLanguage);
      }
      
      // Get the translation
      const translatedText = await aiTranslationService.translateText(
        sourceText,
        columnKey
      );
      
      // Update the UI with the translated text
      if (translatedText) {
        // Use the existing handleTableTranslate to update state and DB
        handleTableTranslate(rowKey, columnKey, translatedText);
        return { result: translatedText };
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };





  if (!selectedTeam) {
    return (
      <EmptyStateEnhanced
        title="No team selected"
        subtitle="Please select a team to view projects and translations."
        fullView={true}
      />
    );
  }

  if (selectedTeam && projects.filter(p => p.team_id === selectedTeam.id).length === 0) {
    return (
      <EmptyStateEnhanced
        title="No projects in this team"
        subtitle="Create your first project to start managing translation content."
        buttonText="Create Project"
        onButtonClick={() => window.dispatchEvent(new CustomEvent('open-create-project-dialog', { detail: { teamId: selectedTeam.id } }))}
        fullView={true}
      />
    );
  }

  if (selectedTeam && projects.filter(p => p.team_id === selectedTeam.id).length > 0 && !selectedProject) {
    return (
      <EmptyStateEnhanced
        title="There are no projects in this team"
        subtitle="Please select a project to view translation keys."
        fullView={true}
      />
    );
  }

  return (
    <>
      {selectedProject && firstLanguage && !isLoading && (
        <LanguageTabs
          languageStats={getMissingTranslationsForAllLanguages}
          sourceLanguage={firstLanguage}
          sourceLanguageName={getLanguageName(firstLanguage)}
          sourceMissingCount={getMissingSourceContentCount()}
          selectedLanguage={selectedLanguage || undefined}
          onLanguageSelect={handleLanguageSelect}
          
          onDeleteLanguageTranslations={handleDeleteLanguageTranslations}
          activeJobs={activeLanguageJobs}
          isAdmin={isAdmin}
          canEditLanguages={canEditLanguages}
          onAddLanguage={handleAddLanguage}
          existingLanguages={availableLanguages}
        />
      )}


      
      <div className="translations-view">
        {selectedProject && firstLanguage && !isLoading && selectedLanguage ? (
          Object.keys(groupedTranslations).length === 0 ? (
            <EmptyStateEnhanced
              title="Wonderful, a new beginning"
              subtitle={searchQuery ? `No keys matching "${searchQuery}" found.` : 'Add your first translation key to get started with localization.'}
              buttonText="Add Key"
              onButtonClick={() => setIsKeyDialogOpen(true)}
              fullView={true}
            />
          ) : (
            <div className="view-container">
              <div className="content">
                {/* Translation Progress Card */}
                {selectedLanguage !== firstLanguage && (
                  (() => {
                    const languageStats = getMissingTranslationsForAllLanguages;
                    const currentLanguageStat = languageStats.find((stat: any) => stat.language === selectedLanguage);
                    const activeJob = activeLanguageJobs.get(selectedLanguage);
                    const isLoading = !!activeJob;
                    const isPreparing = preparingLanguages.has(selectedLanguage);
                    
                    // Use active job progress for real-time accuracy
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for progress UI
                    const completionStateForCard = completionStates.get(selectedLanguage);
                    void completionStateForCard;
                    const progress = activeJob?.progress;
                    
                    if (currentLanguageStat) {
                      // Show preparing state if preparing
                      if (isPreparing) {
                        return (
                          <div className="translation-progress-section">
                            <ContentCard
                              title={`Translate ${getLanguageName(selectedLanguage)}`}
                              subtitle=""
                              buttonText=""
                              onButtonClick={() => {}}
                              totalKeys={currentLanguageStat.totalKeys}
                              isComplete={false}
                              isLoading={false}
                              isPreparing={true}
                              onCancel={() => handleCancelLanguageTranslation(selectedLanguage)}
                            />
                          </div>
                        );
                      }
                      
                      // Show loading state if there's an active job
                      if (isLoading) {
                        return (
                          <div className="translation-progress-section">
                            <ContentCard
                              title={`Translate ${getLanguageName(selectedLanguage)}`}
                              subtitle=""
                              buttonText=""
                              onButtonClick={() => {}}
                              totalKeys={currentLanguageStat.totalKeys}
                              isComplete={false}
                              isLoading={true}
                              onCancel={() => handleCancelLanguageTranslation(selectedLanguage)}
                              progress={progress}
                            />
                          </div>
                        );
                      }
                      
                      // Show translate button if there are missing translations
                      if (currentLanguageStat.missingCount > 0) {
                        return (
                          <div className="translation-progress-section">
                            <ContentCard
                              title={`Translate ${getLanguageName(selectedLanguage)}`}
                              subtitle={`${currentLanguageStat.missingCount} missing translations`}
                              buttonText="Translate"
                              onButtonClick={() => handleTranslateLanguage(selectedLanguage)}
                              totalKeys={currentLanguageStat.totalKeys}
                              isComplete={false}
                              isLoading={false}
                            />
                          </div>
                        );
                      } else if (currentLanguageStat.totalKeys > 0) {
                        // Show completion state
                        return (
                          <div className="translation-progress-section">
                            <ContentCard
                              title={`Translate ${getLanguageName(selectedLanguage)}`}
                              subtitle=""
                              buttonText=""
                              onButtonClick={() => {}}
                              totalKeys={currentLanguageStat.totalKeys}
                              isComplete={true}
                              isLoading={false}
                            />
                          </div>
                        );
                      }
                    }
                    return null;
                  })()
                )}
                
                <div className="translations-section">
                {sortedPrefixes.map((prefix, sectionIndex) => {
                  // Create ref for this section if it doesn't exist
                  if (!sectionRefs.current[prefix]) {
                    sectionRefs.current[prefix] = createRef<HTMLDivElement>();
                  }
                  
                  // Check if this section should be rendered
                  const isRendered = renderedSections.has(sectionIndex);
                  
                  if (!isRendered) {
                    return null; // Don't render anything for non-rendered sections
                  }
                  
                  return (
                    <div key={prefix} ref={sectionRefs.current[prefix]}>
                      <TranslationTableSection
                        prefix={prefix}
                        columns={stableGetColumnsForPrefix(prefix)}
                        data={groupedTranslations[prefix]}
                        getRowKey={stableGetRowKey}
                        getCellContent={stableGetCellContent}
                        onCellChange={handleCellChange}
                        onDelete={handleEditClick}
                        selectedRows={selectedRows}
                        onRowSelect={handleRowSelect}
                        onCellFocus={handleCellFocus}
                        onCellBlur={handleCellBlur}
                        onTranslate={handleTableTranslate}
                        onTranslateCell={handleCellTranslate}
                        targetLanguage={selectedLanguage}
                        sourceLanguage={firstLanguage}
                        onTabNavigation={handleTabNavigation}
                      />
                    </div>
                  );
                })}
                
                {/* Infinite scroll trigger */}
                {renderedSections.size < sortedPrefixes.length && sortedPrefixes.length > 0 && (
                  <div 
                    ref={(el) => {
                      // Update the ref
                      if (loadMoreTriggerRef) {
                        // @ts-ignore - ref.current is mutable, TypeScript sometimes incorrectly flags this
                        loadMoreTriggerRef.current = el;
                      }
                      
                      // Set up observer directly when element is mounted
                      if (el && sortedPrefixes.length > 0 && renderedSections.size < sortedPrefixes.length) {
                        // Clean up existing observer if it exists
                        if (observerRef.current) {
                          observerRef.current.disconnect();
                          observerRef.current = null;
                        }
                        
                        // Create and set up new observer
                        // Use ref to always call the latest loadMoreSections function
                        const observer = new IntersectionObserver(
                          (entries) => {
                            entries.forEach((entry) => {
                              if (entry.isIntersecting && !isLoadingMore.current) {
                                loadMoreSectionsRef.current();
                              }
                            });
                          },
                          {
                            rootMargin: '100px',
                            threshold: 0.1
                          }
                        );
                        
                        observer.observe(el);
                        observerRef.current = observer;
                      } else if (!el) {
                        // Clean up observer when element is unmounted
                        if (observerRef.current) {
                          observerRef.current.disconnect();
                          observerRef.current = null;
                        }
                      }
                    }}
                    style={{ 
                      height: '20px', 
                      width: '100%',
                      marginTop: '20px'
                    }}
                  />
                )}
                
                {/* Loading indicator for infinite scroll */}
                {isLoadingMoreSections && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '20px',
                    color: 'var(--text-secondary)',
                    font: 'var(--font-body)'
                  }}>
                    Loading more translations...
                  </div>
                )}
              </div>
            </div>
          </div>
          )
        ) : null}

        {!selectedTeam && (
          <EmptyStateEnhanced
            title="No team selected"
            subtitle="Please select a team to view projects and translations."
            fullView={true}
          />
        )}

        {selectedTeam && projects.filter(p => p.team_id === selectedTeam.id).length === 0 && (
          <EmptyStateEnhanced
            title="No projects in this team"
            subtitle="Create your first project to start managing translation content."
            buttonText="Create Project"
            onButtonClick={() => window.dispatchEvent(new CustomEvent('open-create-project-dialog', { detail: { teamId: selectedTeam.id } }))}
            fullView={true}
          />
        )}

        {selectedTeam && projects.filter(p => p.team_id === selectedTeam.id).length > 0 && !selectedProject && (
          <EmptyStateEnhanced
            title="There are no projects in this team"
            subtitle="Please select a project to view translation keys."
            fullView={true}
          />
        )}
        
        {error && (
          <EmptyStateEnhanced
            title="Error"
            subtitle={`${error}`}
            fullView={true}
          />
        )}

        {selectedProject && isLoading && (
          <div className="loading-container">
            <Button
              variant="text"
              size="large"
              isLoading={true}
              disabled={true}
            >
            </Button>
          </div>
        )}
        

      </div>

      <TranslationKeyDialog
        isOpen={isKeyDialogOpen}
        onClose={() => setIsKeyDialogOpen(false)}
        onSave={handleCreateKey}
        isLoading={isCreatingKey}
        firstLanguage={firstLanguage}
      />

      {keyToEdit && (
        <EditKeyDialog
          isOpen={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setKeyToEdit(null);
          }}
          onSave={handleSaveKeyEdit}
          onDelete={handleDeleteKey}
          keyName={keyToEdit.key}
          isLoading={isEditingKey}
        />
      )}

      <ReviewDialog
        isOpen={isReviewDialogOpen}
        onClose={() => setIsReviewDialogOpen(false)}
        onSave={handleSaveReview}
        isLoading={isCreatingReview}
        selectedKeys={selectedKeyNames}
      />

      <SelectReviewDialog
        isOpen={isSelectReviewDialogOpen}
        onClose={() => setIsSelectReviewDialogOpen(false)}
        onSelect={handleReviewSelect}
        projectId={selectedProject?.id || ''}
        isLoading={isAddingToReview}
      />

      {/* Only render ImportTranslationsDialog if selectedProject is defined */}
      {selectedProject && (
        <ImportTranslationsDialog
          isOpen={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          projectId={selectedProject.id}
          onImportComplete={handleImportComplete}
          isLoading={isImporting}
        />
      )}

      {/* Only render ExportTranslationsDialog if selectedProject is defined */}
      {selectedProject && (
        <ExportTranslationsDialog
          isOpen={isExportDialogOpen}
          onClose={() => setIsExportDialogOpen(false)}
          projectId={selectedProject.id}
          projectLanguages={selectedProject.languages || []}
          isLoading={isExporting}
        />
      )}

      <SelectionActionBar
        selectedCount={selectedRows.size}
        onCreateReview={handleCreateReview}
        onAddToReview={handleAddToReview}
        onDeselectAll={handleDeselectAll}
        isVisible={selectedRows.size > 0}
      />
      <SubscriptionDialog
        isOpen={isSubscriptionDialogOpen}
        onClose={() => setIsSubscriptionDialogOpen(false)}
      />
    </>
  );
}, () => {
  // TranslationsView doesn't take props, so always return true to prevent re-renders
  return true;
});

export default TranslationsView; 